import DOMPurify from 'dompurify';
import { marked } from 'marked';
import TurndownService from 'turndown';
import { isApmFileId } from './apm-file';

export const FONT_SIZES = [10, 12, 14, 16, 18, 20, 24, 28, 32] as const;

/** Markdown supports inline HTML for the two styles without native syntax. */
export function sanitizeRichText(html: string): string {
  return sanitize(html, {});
}

function sanitize(html: string, options: RichTextOptions): string {
  const fragment = DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      'p',
      'br',
      'strong',
      'b',
      'em',
      'i',
      's',
      'del',
      'u',
      'span',
      'ul',
      'ol',
      'li',
      'a',
      'blockquote',
      'pre',
      'code',
      'h1',
      'h2',
      'h3',
      'h4',
      'h5',
      'h6',
      'hr',
      // Bảng GFM của `marked` — thiếu chúng thì bảng thành một dòng chữ dính liền.
      'table',
      'thead',
      'tbody',
      'tr',
      'th',
      'td',
      ...(options.images ? ['img'] : []),
    ],
    ALLOWED_ATTR: [
      'href',
      'title',
      'start',
      'style',
      // Căn cột `:-:` / `--:`: `align` từ `marked`, `data-align` từ ô của editor khi lưu — cả
      // hai quy về `data-align` ngay bên dưới.
      'align',
      'data-align',
      // Chỉ để giữ `language-*` của khối code — lớp khác bị gỡ ngay bên dưới.
      'class',
      // `data-file-id` lọt qua dù `ALLOW_DATA_ATTR: false` vì được liệt kê tường minh.
      ...(options.images ? ['src', 'alt', 'data-file-id'] : []),
    ],
    ALLOW_DATA_ATTR: false,
    ALLOW_ARIA_ATTR: false,
    RETURN_DOM_FRAGMENT: true,
  });
  fragment.querySelectorAll('[style]').forEach((element) => {
    const size = (element as HTMLElement).style.fontSize;
    element.removeAttribute('style');
    if (element.tagName === 'SPAN' && FONT_SIZES.some((value) => size === `${value}px`)) {
      element.setAttribute('style', `font-size: ${size}`);
    }
  });
  // CSS bảng (và ô của editor) đọc `data-align`; chỉ ô bảng, chỉ giữa/phải — trái là mặc định.
  fragment.querySelectorAll('[align], [data-align]').forEach((element) => {
    const align = element.getAttribute('data-align') ?? element.getAttribute('align');
    element.removeAttribute('align');
    element.removeAttribute('data-align');
    if ((element.tagName === 'TH' || element.tagName === 'TD') && isCellAlign(align)) {
      element.setAttribute('data-align', align);
    }
  });
  // Ngôn ngữ của khối code (```mermaid, APM-MMD-a) đi qua lớp `language-*`: gỡ nó là
  // sơ đồ thành chữ, và lưu lại từ hộp soạn là mất luôn nhãn ngôn ngữ trong tệp.
  fragment.querySelectorAll('[class]').forEach((element) => {
    const language = element.tagName === 'CODE' ? codeLanguageClass(element) : null;
    if (language) element.setAttribute('class', language);
    else element.removeAttribute('class');
  });
  const container = document.createElement('div');
  container.append(fragment);
  return container.innerHTML;
}

function isCellAlign(value: string | null): value is 'center' | 'right' {
  return value === 'center' || value === 'right';
}

/** `language-xxx` đầu tiên của một `<code>` — chữ, số, `+ - _ .` như tên ngôn ngữ thật. */
function codeLanguageClass(element: Element): string | null {
  for (const name of element.classList) {
    if (/^language-[\w+.-]{1,40}$/.test(name)) return name;
  }
  return null;
}

export interface RichTextOptions {
  /**
   * Keep `<img src alt data-file-id>` — only for a personal `.md` document:
   * the file preview (APM-MYF-d) and the document editor (APM-MYF-f), whose
   * `apm-file:` references were already swapped for presigned URLs **before**
   * this runs (DOMPurify drops unknown protocols). Rich-text fields elsewhere
   * (to-do, task, project) stay image-free.
   */
  readonly images?: boolean;
}

export function markdownToHtml(markdown: string, options: RichTextOptions = {}): string {
  return sanitize(marked.parse(markdown, { async: false, gfm: true, breaks: true }), options);
}

const converter = new TurndownService({
  headingStyle: 'atx',
  bulletListMarker: '-',
  codeBlockStyle: 'fenced',
  emDelimiter: '*',
});
// Literal HTML typed by the user must not become an HTML node on the next load.
const escapeMarkdown = converter.escape.bind(converter);
converter.escape = (text) =>
  escapeMarkdown(text).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
converter.addRule('strike', {
  filter: ['s', 'del'],
  replacement: (content) => `~~${content}~~`,
});
converter.addRule('underline', {
  filter: 'u',
  replacement: (content) => `<u>${content}</u>`,
});
converter.addRule('fontSize', {
  filter: (node) => node.nodeName === 'SPAN' && Boolean((node as HTMLElement).style.fontSize),
  replacement: (content, node) =>
    `<span style="font-size: ${(node as HTMLElement).style.fontSize}">${content}</span>`,
});

/**
 * Images (only present with `images: true`, see `htmlToMarkdown`): an image
 * that came from "Tệp của tôi" is written back as `apm-file:{id}` — never its
 * presigned `src`, which expires after 60 minutes (docs/plan-my-files.md §2.5).
 * A plain http(s) image is kept as-is; anything else (`data:`, `blob:`) is
 * dropped so base64 blobs never end up inside the document.
 */
converter.addRule('image', {
  filter: 'img',
  replacement: (_content, node) => {
    const image = node as HTMLImageElement;
    const fileId = image.getAttribute('data-file-id');
    const src = image.getAttribute('src') ?? '';
    const target = isApmFileId(fileId)
      ? `apm-file:${fileId.toLowerCase()}`
      : /^https?:\/\//i.test(src)
        ? src.replace(/[()\s]/g, encodeURIComponent)
        : null;
    if (!target) return '';
    const alt = markdownLabel(image.getAttribute('alt') ?? '');
    const title = image.getAttribute('title');
    return `![${alt}](${target}${title ? ` "${title.replace(/"/g, '\\"')}"` : ''})`;
  },
});

/**
 * Tables are written back as GFM: without this rule Turndown keeps only the cell
 * text, so opening a `.md` with a table in the editor and saving it deletes the
 * table. GFM has no merged cells or multi-line cells — a line break inside a cell
 * becomes `<br>` (which `marked` reads back), and a short row is padded.
 */
converter.addRule('table', {
  filter: 'table',
  replacement: (_content, node) => `\n\n${gfmTable(node as HTMLTableElement)}\n\n`,
});

const ALIGN_RULE: Readonly<Record<string, string>> = { center: ':-:', right: '--:' };

function gfmTable(table: HTMLTableElement): string {
  const rows = [...table.rows];
  const [head, ...body] = rows;
  if (!head) return '';
  const width = Math.max(...rows.map((row) => row.cells.length));
  const pad = (cells: string[]): string[] => [
    ...cells,
    ...Array.from({ length: width - cells.length }, () => ''),
  ];
  const line = (cells: string[]): string => `| ${pad(cells).join(' | ')} |`;
  const rule = pad(
    [...head.cells].map((cell) => ALIGN_RULE[cell.getAttribute('data-align') ?? ''] ?? '---'),
  ).map((cell) => cell || '---');
  return [
    line([...head.cells].map(cellMarkdown)),
    `| ${rule.join(' | ')} |`,
    ...body.map((row) => line([...row.cells].map(cellMarkdown))),
  ].join('\n');
}

function cellMarkdown(cell: HTMLTableCellElement): string {
  return converter
    .turndown(cell.innerHTML)
    .trim()
    .replace(/[ \t]*\n+[ \t]*/g, '<br>')
    .replace(/\|/g, '\\|');
}

/** Alt text inside `![…]`: one line, brackets and backslashes escaped. */
function markdownLabel(text: string): string {
  return text
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/[\\[\]]/g, '\\$&');
}

export function htmlToMarkdown(html: string, options: RichTextOptions = {}): string {
  return converter.turndown(sanitize(html, options));
}
